import { Component, effect, inject, type OnInit } from "@angular/core";
import {
  GloTabContentDirective,
  GloTabsComponent,
  type TabItem,
} from "../../../shared/components/tabs/tabs.component";
import { GloPaginationComponent } from "../../../shared/components/pagination/pagination.component";
import { GloControlComponent } from "../../../shared/components/glo-control/glo-control.component";
import { MatDialog } from "@angular/material/dialog";
import { DialogPositionClass } from "../../../shared/enums/dialogs.enum";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { HttpClientModule } from "@angular/common/http";
import { forkJoin, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import dayjs from "dayjs";
import { AppointmentsAddComponent } from "../appointments-add/appointments-add/appointments-add.component";
import { Appointment } from "../../../shared/models/appointment.model";
import Swal from "sweetalert2";
import { ApiService } from "../../../shared/services/api/api.service";
import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";
import { ActivatedRoute } from "@angular/router";
import { environment } from "../../../../environments/environment";
import { BusinessSalonContextService } from "../../../shared/services/api/business-salon-context.service";

interface SalonOption {
  id: string;
  name: string;
}

interface WorkingWindow {
  start: string;
  end: string;
  isOff: boolean;
}

interface TimeOffWindow {
  start: Date;
  end: Date;
}

interface ScheduleStaff {
  id: string;
  alternateIds: string[];
  salonId: string;
  salonName: string;
  name: string;
  email: string;
  jobTitle: string;
  raw: any;
  workingHours: WorkingWindow | null;
  timeOff: TimeOffWindow[];
}

type CalendarView = "day" | "week" | "month";

@Component({
  selector: "business-appointments",
  imports: [
    GloTabsComponent,
    GloTabContentDirective,
    FormsModule,
    CommonModule,
    HttpClientModule,
    GloPaginationComponent,
    GloControlComponent,
  ],
  templateUrl: "./appointments.component.html",
  styleUrl: "./appointments.component.scss",
})
export class BusinessAppointmentsComponent implements OnInit {
  dialog: MatDialog = inject(MatDialog);

  assetUrl = environment.assetBaseUrl;

  readonly salonContext = inject(BusinessSalonContextService);

  readonly tabs: TabItem[] = [
    {
      label: "Schedule",
      key: "calendar",
    },
    {
      label: "Table View",
      key: "table",
    },
  ];

  private _activeTab = "calendar";

  get activeTab(): string {
    return this._activeTab;
  }

  set activeTab(value: string) {
    this._activeTab = value || "calendar";
  }

  activeTabChange(tab: string): void {
    this.activeTab = tab;
  }

  /* =========================================================
     Appointments
     ========================================================= */

  appointmentsData: Appointment[] = [];
  filteredAppointments: Appointment[] = [];
  paginatedAppointments: Appointment[] = [];

  isLoading = false;

  error: string | null = null;

  searchTerm = "";

  currentPage = 1;

  itemsPerPage = 10;

  totalPages = 0;

  totalItems = 0;

  /* =========================================================
     Filters
     ========================================================= */

  salons: SalonOption[] = [];

  selectedSalonId = "all";

  selectedType = "all";

  /* =========================================================
     Detail modal
     ========================================================= */

  showModal = false;

  selectedAppointment: Appointment | null = null;

  isUpdatingStatus = false;

  /* =========================================================
     Calendar
     ========================================================= */

  calendarView: CalendarView = "day";

  scheduleDate = new Date();

  scheduleDateInput = dayjs().format("YYYY-MM-DD");

  weekDays: Date[] = [];

  monthDays: Date[] = [];

  /* =========================================================
     Daily scheduler
     ========================================================= */

  scheduleStaff: ScheduleStaff[] = [];

  scheduleSlots: Date[] = [];

  isScheduleLoading = false;

  scheduleError: string | null = null;

  readonly slotMinutes = 30;

  readonly slotHeight = 54;

  readonly scheduleStartHour = 7;

  readonly scheduleEndHour = 21;

  /* =========================================================
     Drag state
     ========================================================= */

  draggedAppointment: Appointment | null = null;

  dragOverStaffId: string | null = null;

  dragOverSlotKey: string | null = null;

  isMovingAppointment = false;

  constructor(
    private ApiService: ApiService,
    private route: ActivatedRoute,
  ) {
    effect(() => {
      const salons = this.salonContext.salons();

      this.salonContext.selectedSalonIds();

      if (!salons.length) {
        return;
      }

      this.syncLocalSalonFilter();

      this.buildSalonOptions();

      this.loadAppointments();

      if (this.calendarView === "day") {
        this.loadScheduleStaff();
      }
    });
  }

  ngOnInit(): void {
    this.rebuildCalendar();

    this.route.queryParams.subscribe((param) => {
      if (param["view"]) {
        this.activeTabChange(param["view"]);
      }
    });
  }

  /* =========================================================
     Calendar headings
     ========================================================= */

  get scheduleTitle(): string {
    if (this.calendarView === "week") {
      const start = dayjs(this.weekDays[0]);

      const end = dayjs(this.weekDays[this.weekDays.length - 1]);

      if (start.month() === end.month()) {
        return `${start.format("MMMM D")} – ${end.format("D, YYYY")}`;
      }

      return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
    }

    if (this.calendarView === "month") {
      return dayjs(this.scheduleDate).format("MMMM YYYY");
    }

    return dayjs(this.scheduleDate).format("dddd, MMMM D, YYYY");
  }

  get previousButtonTitle(): string {
    switch (this.calendarView) {
      case "week":
        return "Previous week";

      case "month":
        return "Previous month";

      default:
        return "Previous day";
    }
  }

  get nextButtonTitle(): string {
    switch (this.calendarView) {
      case "week":
        return "Next week";

      case "month":
        return "Next month";

      default:
        return "Next day";
    }
  }

  /* =========================================================
     Load appointments
     ========================================================= */

  loadAppointments(): void {
    this.isLoading = true;

    this.error = null;

    const url = this.salonContext.withSalonScope(
      API_ENDPOINTS.owner.appointments.list,
    );

    this.ApiService.request(url, "GET").subscribe({
      next: (response: any) => {
        this.appointmentsData = this.extractArray(response) as Appointment[];

        this.buildSalonOptions();

        this.currentPage = 1;

        this.applyFilters();

        this.isLoading = false;
      },

      error: (error) => {
        console.error("Error loading appointments:", error);

        this.appointmentsData = [];

        this.filteredAppointments = [];

        this.paginatedAppointments = [];

        this.totalItems = 0;

        this.totalPages = 0;

        this.error = "Failed to load appointments. Please try again.";

        this.isLoading = false;
      },
    });
  }

  /* =========================================================
     KPI
     ========================================================= */

  get pendingCount(): number {
    return this.appointmentsData.filter(
      (appointment) =>
        this.normalizeAppointmentStatus(appointment.status) === "pending",
    ).length;
  }

  get confirmedCount(): number {
    return this.appointmentsData.filter(
      (appointment) =>
        this.normalizeAppointmentStatus(appointment.status) === "confirmed",
    ).length;
  }

  get completedCount(): number {
    return this.appointmentsData.filter(
      (appointment) =>
        this.normalizeAppointmentStatus(appointment.status) === "completed",
    ).length;
  }

  /* =========================================================
     Salon options
     ========================================================= */

  buildSalonOptions(): void {
    const selectedIds = new Set(
      this.salonContext.selectedSalonIds().map(String),
    );

    this.salons = this.salonContext
      .salons()
      .filter((salon) => selectedIds.has(String(salon.salon_id)))
      .map((salon) => ({
        id: String(salon.salon_id),

        name: salon.name || "Salon",
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private syncLocalSalonFilter(): void {
    const selectedIds = new Set(
      this.salonContext.selectedSalonIds().map(String),
    );

    if (
      this.selectedSalonId !== "all" &&
      !selectedIds.has(this.selectedSalonId)
    ) {
      this.selectedSalonId = "all";
    }
  }

  /* =========================================================
     Filters
     ========================================================= */

  onSearch(searchValue: string): void {
    this.searchTerm = (searchValue || "").toLowerCase();

    this.currentPage = 1;

    this.applyFilters();
  }

  onChangeSalon(event: Event): void {
    this.selectedSalonId = (event.target as HTMLSelectElement).value || "all";

    this.currentPage = 1;

    this.applyFilters();

    if (this.calendarView === "day") {
      this.loadScheduleStaff();
    }
  }

  onChangeType(event: Event): void {
    this.selectedType = (event.target as HTMLSelectElement).value || "all";

    this.currentPage = 1;

    this.applyFilters();
  }

  applyFilters(): void {
    this.filteredAppointments = this.appointmentsData.filter(
      (appointment: any) => {
        const bookingId = String(
          appointment?.appointment_id || "",
        ).toLowerCase();

        const staffName = (appointment?.staff?.full_name || "").toLowerCase();

        const staffEmail = (appointment?.staff?.email || "").toLowerCase();

        const customerName = (
          appointment?.customer?.full_name || ""
        ).toLowerCase();

        const status = (appointment?.status || "").toLowerCase();

        const salonName = this.getSalonName(appointment).toLowerCase();

        const searchMatch =
          !this.searchTerm ||
          bookingId.includes(this.searchTerm) ||
          staffName.includes(this.searchTerm) ||
          staffEmail.includes(this.searchTerm) ||
          customerName.includes(this.searchTerm) ||
          status.includes(this.searchTerm) ||
          salonName.includes(this.searchTerm);

        const salonMatch =
          this.selectedSalonId === "all" ||
          this.getSalonId(appointment) === this.selectedSalonId;

        const typeMatch =
          this.selectedType === "all" ||
          this.normalizeAppointmentStatus(status) ===
            this.normalizeAppointmentStatus(this.selectedType);

        return searchMatch && salonMatch && typeMatch;
      },
    );

    this.totalItems = this.filteredAppointments.length;

    this.updatePagination();
  }

  /* =========================================================
     Pagination
     ========================================================= */

  updatePagination(): void {
    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);

    const startIndex = (this.currentPage - 1) * this.itemsPerPage;

    const endIndex = startIndex + this.itemsPerPage;

    this.paginatedAppointments = this.filteredAppointments.slice(
      startIndex,
      endIndex,
    );
  }

  onPageChange(page: number): void {
    this.currentPage = page;

    this.updatePagination();
  }

  onItemsPerPageChange(limit: number): void {
    this.itemsPerPage = limit;

    this.currentPage = 1;

    this.updatePagination();
  }

  /* =========================================================
     Calendar view
     ========================================================= */

  onCalendarViewChange(value: CalendarView): void {
    this.calendarView = value || "day";

    this.rebuildCalendar();

    if (this.calendarView === "day") {
      this.loadScheduleStaff();
    }
  }

  onScheduleDateInput(value: string): void {
    if (!value) {
      return;
    }

    this.setScheduleDate(dayjs(value).startOf("day").toDate());
  }

  previousPeriod(): void {
    switch (this.calendarView) {
      case "week":
        this.setScheduleDate(
          dayjs(this.scheduleDate).subtract(1, "week").toDate(),
        );

        break;

      case "month":
        this.setScheduleDate(
          dayjs(this.scheduleDate).subtract(1, "month").toDate(),
        );

        break;

      default:
        this.setScheduleDate(
          dayjs(this.scheduleDate).subtract(1, "day").toDate(),
        );
    }
  }

  nextPeriod(): void {
    switch (this.calendarView) {
      case "week":
        this.setScheduleDate(dayjs(this.scheduleDate).add(1, "week").toDate());

        break;

      case "month":
        this.setScheduleDate(dayjs(this.scheduleDate).add(1, "month").toDate());

        break;

      default:
        this.setScheduleDate(dayjs(this.scheduleDate).add(1, "day").toDate());
    }
  }

  goToToday(): void {
    this.setScheduleDate(new Date());
  }

  openDay(date: Date): void {
    this.calendarView = "day";

    this.setScheduleDate(date);
  }

  private setScheduleDate(date: Date): void {
    this.scheduleDate = dayjs(date).startOf("day").toDate();

    this.scheduleDateInput = dayjs(this.scheduleDate).format("YYYY-MM-DD");

    this.rebuildCalendar();

    if (this.calendarView === "day") {
      this.loadScheduleStaff();
    }
  }

  private rebuildCalendar(): void {
    this.buildScheduleSlots();

    this.buildWeekDays();

    this.buildMonthDays();
  }

  /* =========================================================
     Week
     ========================================================= */

  private buildWeekDays(): void {
    const start = dayjs(this.scheduleDate).startOf("week");

    this.weekDays = Array.from({ length: 7 }, (_, index) =>
      start.add(index, "day").toDate(),
    );
  }

  /* =========================================================
     Month
     ========================================================= */

  private buildMonthDays(): void {
    const monthStart = dayjs(this.scheduleDate).startOf("month");

    const gridStart = monthStart.startOf("week");

    this.monthDays = Array.from({ length: 42 }, (_, index) =>
      gridStart.add(index, "day").toDate(),
    );
  }

  isToday(date: Date): boolean {
    return dayjs(date).isSame(dayjs(), "day");
  }

  isCurrentMonth(date: Date): boolean {
    return dayjs(date).isSame(this.scheduleDate, "month");
  }

  getAppointmentsForDate(date: Date): Appointment[] {
    const target = dayjs(date).format("YYYY-MM-DD");

    return this.filteredAppointments
      .filter(
        (appointment: any) =>
          dayjs(appointment.start_time).format("YYYY-MM-DD") === target,
      )
      .sort(
        (a: any, b: any) =>
          dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf(),
      );
  }

  getVisibleMonthAppointments(date: Date): Appointment[] {
    return this.getAppointmentsForDate(date).slice(0, 3);
  }

  getMonthOverflowCount(date: Date): number {
    return Math.max(0, this.getAppointmentsForDate(date).length - 3);
  }

  getCalendarBookingClass(appointment: any): string[] {
    const status = this.normalizeAppointmentStatus(appointment?.status);

    return [`calendar-booking--${status || "default"}`];
  }

  /* =========================================================
     Staff loading
     ========================================================= */

  private loadScheduleStaff(): void {
    const salonIds =
      this.selectedSalonId === "all"
        ? this.salonContext.selectedSalonIds().map(String)
        : [this.selectedSalonId];

    if (!salonIds.length) {
      this.scheduleStaff = [];

      this.isScheduleLoading = false;

      return;
    }

    this.isScheduleLoading = true;

    this.scheduleError = null;

    const requests = salonIds.map((salonId) =>
      this.ApiService.request(
        API_ENDPOINTS.staff.listAcrossSalon(salonId),
        "GET",
      ).pipe(
        map((response: any) => ({
          salonId,

          staff: this.extractArray(response),
        })),

        catchError((error) => {
          console.error(`Error loading staff for salon ${salonId}:`, error);

          return of({
            salonId,

            staff: [] as any[],
          });
        }),
      ),
    );

    forkJoin(requests).subscribe({
      next: (groups) => {
        const staff = groups.flatMap((group) =>
          group.staff
            .filter((item: any) => item?.is_active !== false)
            .map((item: any) => this.normalizeStaff(item, group.salonId)),
        );

        const unique = new Map<string, ScheduleStaff>();

        for (const item of staff) {
          unique.set(`${item.salonId}:${item.id}`, item);
        }

        this.scheduleStaff = Array.from(unique.values()).sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        this.loadAvailabilityForScheduleStaff();
      },

      error: (error) => {
        console.error("Error loading schedule staff:", error);

        this.scheduleStaff = [];

        this.scheduleError = "Failed to load employees for the schedule.";

        this.isScheduleLoading = false;
      },
    });
  }

  /* =========================================================
     Staff normalization
     ========================================================= */

  private normalizeStaff(item: any, salonId: string): ScheduleStaff {
    const ids = this.collectIds([
      item?.resolved_user_id,
      item?.user_id,
      item?.staff_id,
      item?.employee_id,
      item?.id,
      item?.user?.user_id,
      item?.user?.id,
      typeof item?.user === "string" ? item.user : null,
    ]);

    const id = ids[0] || "";

    const salon = this.salons.find((option) => option.id === salonId);

    return {
      id,

      alternateIds: ids,

      salonId,

      salonName:
        item?.salon_name || item?.salon?.name || salon?.name || "Salon",

      name:
        item?.full_name ||
        item?.user_full_name ||
        item?.user?.full_name ||
        item?.name ||
        item?.email ||
        "Staff Member",

      email: item?.email || item?.user_email || item?.user?.email || "",

      jobTitle: item?.job_title || item?.role || "Staff Member",

      raw: item,

      workingHours: null,

      timeOff: [],
    };
  }

  private collectIds(values: any[]): string[] {
    return Array.from(
      new Set(
        values
          .filter(
            (value) =>
              value !== null &&
              value !== undefined &&
              String(value).trim() !== "",
          )
          .map((value) => String(value)),
      ),
    );
  }

  /* =========================================================
     Staff availability
     ========================================================= */

  private loadAvailabilityForScheduleStaff(): void {
    if (!this.scheduleStaff.length) {
      this.isScheduleLoading = false;

      return;
    }

    const requests = this.scheduleStaff.map((staff) =>
      forkJoin({
        working: this.ApiService.request(
          API_ENDPOINTS.staffAvailability.workingHoursList(
            staff.salonId,
            staff.id,
          ),
          "GET",
        ).pipe(catchError(() => of([]))),

        timeOff: this.ApiService.request(
          API_ENDPOINTS.staffAvailability.timeOffList(staff.salonId, staff.id),
          "GET",
        ).pipe(catchError(() => of([]))),
      }).pipe(
        map(({ working, timeOff }) => ({
          staffId: staff.id,

          salonId: staff.salonId,

          working,

          timeOff,
        })),
      ),
    );

    forkJoin(requests).subscribe({
      next: (availability) => {
        this.scheduleStaff = this.scheduleStaff.map((staff) => {
          const data = availability.find(
            (item) =>
              item.staffId === staff.id && item.salonId === staff.salonId,
          );

          return {
            ...staff,

            workingHours: this.resolveWorkingHours(data?.working),

            timeOff: this.resolveTimeOff(data?.timeOff),
          };
        });

        this.isScheduleLoading = false;
      },

      error: (error) => {
        console.error("Error loading staff availability:", error);

        this.scheduleError =
          "Staff loaded, but availability could not be loaded.";

        this.isScheduleLoading = false;
      },
    });
  }

  private resolveWorkingHours(response: any): WorkingWindow | null {
    const rows = this.extractArray(response);

    const weekday = this.getPythonWeekday(this.scheduleDate);

    const row = rows.find((item: any) => {
      const value =
        item?.day_of_week ?? item?.weekday ?? item?.day ?? item?.dayOfWeek;

      if (typeof value === "number") {
        return value === weekday;
      }

      const names = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];

      return String(value || "").toLowerCase() === names[weekday];
    });

    if (!row) {
      return null;
    }

    return {
      start: row?.start_time || row?.start || row?.startTime || "00:00:00",

      end: row?.end_time || row?.end || row?.endTime || "00:00:00",

      isOff:
        row?.is_off === true ||
        row?.isOff === true ||
        row?.is_working === false,
    };
  }

  private resolveTimeOff(response: any): TimeOffWindow[] {
    return this.extractArray(response)
      .filter(
        (item: any) => item?.is_active !== false && item?.active !== false,
      )
      .map((item: any) => {
        const start =
          item?.start_at || item?.start_time || item?.start || item?.startAt;

        const end = item?.end_at || item?.end_time || item?.end || item?.endAt;

        return {
          start: new Date(start),

          end: new Date(end),
        };
      })
      .filter(
        (item) =>
          !Number.isNaN(item.start.getTime()) &&
          !Number.isNaN(item.end.getTime()),
      );
  }

  /* =========================================================
     Generic API-array extraction
     ========================================================= */

  private extractArray(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.results)) {
      return response.results;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.staff)) {
      return response.staff;
    }

    if (Array.isArray(response?.appointments)) {
      return response.appointments;
    }

    if (Array.isArray(response?.working_hours)) {
      return response.working_hours;
    }

    if (Array.isArray(response?.time_off)) {
      return response.time_off;
    }

    return [];
  }

  /* =========================================================
     Daily slots
     ========================================================= */

  private buildScheduleSlots(): void {
    const slots: Date[] = [];

    const date = dayjs(this.scheduleDate)
      .hour(this.scheduleStartHour)
      .minute(0)
      .second(0)
      .millisecond(0);

    const totalMinutes = (this.scheduleEndHour - this.scheduleStartHour) * 60;

    for (let minutes = 0; minutes < totalMinutes; minutes += this.slotMinutes) {
      slots.push(date.add(minutes, "minute").toDate());
    }

    this.scheduleSlots = slots;
  }

  private getPythonWeekday(date: Date): number {
    const jsDay = date.getDay();

    return jsDay === 0 ? 6 : jsDay - 1;
  }

  formatScheduleTime(date: Date): string {
    return dayjs(date).format("h:mm A");
  }

  getSlotKey(date: Date): string {
    return dayjs(date).format("YYYY-MM-DD-HH-mm");
  }

  /* =========================================================
     Working hours
     ========================================================= */

  isStaffWorkingAt(staff: ScheduleStaff, slot: Date): boolean {
    const working = staff.workingHours;

    if (!working || working.isOff) {
      return false;
    }

    const slotMinutes = slot.getHours() * 60 + slot.getMinutes();

    const start = this.timeStringToMinutes(working.start);

    const end = this.timeStringToMinutes(working.end);

    return slotMinutes >= start && slotMinutes + this.slotMinutes <= end;
  }

  isStaffOnTimeOffAt(staff: ScheduleStaff, slot: Date): boolean {
    const slotStart = slot.getTime();

    const slotEnd = slotStart + this.slotMinutes * 60 * 1000;

    return staff.timeOff.some(
      (timeOff) =>
        timeOff.start.getTime() < slotEnd && timeOff.end.getTime() > slotStart,
    );
  }

  getWorkingHoursLabel(staff: ScheduleStaff): string {
    if (!staff.workingHours || staff.workingHours.isOff) {
      return "Not working";
    }

    return `${this.formatClockString(
      staff.workingHours.start,
    )} – ${this.formatClockString(staff.workingHours.end)}`;
  }

  private timeStringToMinutes(value: string): number {
    const [hours, minutes] = String(value || "00:00")
      .split(":")
      .map(Number);

    return (hours || 0) * 60 + (minutes || 0);
  }

  private formatClockString(value: string): string {
    const [hours, minutes] = String(value || "00:00")
      .split(":")
      .map(Number);

    return dayjs()
      .hour(hours || 0)
      .minute(minutes || 0)
      .format("h:mm A");
  }

  /* =========================================================
     IMPORTANT:
     Match appointment -> schedule staff
     ========================================================= */

  getAppointmentsForStaff(staff: ScheduleStaff): Appointment[] {
    const targetDate = dayjs(this.scheduleDate).format("YYYY-MM-DD");

    return this.filteredAppointments
      .filter((appointment: any) => {
        const appointmentDate = dayjs(appointment.start_time).format(
          "YYYY-MM-DD",
        );

        if (appointmentDate !== targetDate) {
          return false;
        }

        if (this.getSalonId(appointment) !== staff.salonId) {
          return false;
        }

        return this.appointmentBelongsToStaff(appointment, staff);
      })
      .sort(
        (a: any, b: any) =>
          dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf(),
      );
  }

  private appointmentBelongsToStaff(
    appointment: any,
    staff: ScheduleStaff,
  ): boolean {
    const appointmentIds = this.getAppointmentStaffIds(appointment);

    if (appointmentIds.some((id) => staff.alternateIds.includes(id))) {
      return true;
    }

    const appointmentEmail = String(appointment?.staff?.email || "")
      .trim()
      .toLowerCase();

    const staffEmail = String(staff.email || "")
      .trim()
      .toLowerCase();

    if (appointmentEmail && staffEmail && appointmentEmail === staffEmail) {
      return true;
    }

    const appointmentName = String(appointment?.staff?.full_name || "")
      .trim()
      .toLowerCase();

    const staffName = String(staff.name || "")
      .trim()
      .toLowerCase();

    return !!appointmentName && !!staffName && appointmentName === staffName;
  }

  private getAppointmentStaffIds(appointment: any): string[] {
    return this.collectIds([
      appointment?.staff_id,

      appointment?.staff?.staff_id,

      appointment?.staff?.user_id,

      appointment?.staff?.resolved_user_id,

      appointment?.staff?.id,

      typeof appointment?.staff === "string" ? appointment.staff : null,
    ]);
  }

  private getPrimaryAppointmentStaffId(appointment: any): string {
    return this.getAppointmentStaffIds(appointment)[0] || "";
  }

  /* =========================================================
     Appointment placement
     ========================================================= */

  getAppointmentTop(appointment: any): number {
    const start = dayjs(appointment.start_time);

    const minutesFromStart =
      (start.hour() - this.scheduleStartHour) * 60 + start.minute();

    return Math.max(0, (minutesFromStart / this.slotMinutes) * this.slotHeight);
  }

  getAppointmentHeight(appointment: any): number {
    const start = dayjs(appointment.start_time);

    const end = dayjs(appointment.end_time);

    const minutes = Math.max(
      this.slotMinutes,

      end.diff(start, "minute") ||
        Number(appointment?.total_duration_minutes) ||
        this.slotMinutes,
    );

    return Math.max(44, (minutes / this.slotMinutes) * this.slotHeight - 4);
  }

  getScheduleAppointmentClass(appointment: any): string[] {
    const status = this.normalizeAppointmentStatus(appointment?.status);

    return [
      `schedule-appointment--${status || "default"}`,

      !this.canDragAppointment(appointment)
        ? "schedule-appointment--locked"
        : "",
    ].filter(Boolean);
  }

  canDragAppointment(appointment: any): boolean {
    return !["cancelled", "completed", "no_show"].includes(
      this.normalizeAppointmentStatus(appointment?.status),
    );
  }

  getCompactServiceNames(appointment: any): string {
    const names = (appointment?.items || [])
      .map((item: any) => item?.service?.name)
      .filter(Boolean);

    return names.length ? names.join(", ") : "Service";
  }

  getAppointmentTooltip(appointment: any): string {
    return [
      appointment?.customer?.full_name || "Appointment",

      this.getCompactServiceNames(appointment),

      `${this.formatTime(appointment.start_time)} – ${this.formatTime(
        appointment.end_time,
      )}`,
    ].join(" • ");
  }

  /* =========================================================
     Drag/drop
     ========================================================= */

  onAppointmentDragStart(event: DragEvent, appointment: Appointment): void {
    if (!this.canDragAppointment(appointment)) {
      event.preventDefault();

      return;
    }

    this.draggedAppointment = appointment;

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";

      event.dataTransfer.setData(
        "text/plain",
        String((appointment as any).appointment_id),
      );
    }
  }

  onAppointmentDragEnd(): void {
    this.draggedAppointment = null;

    this.dragOverStaffId = null;

    this.dragOverSlotKey = null;
  }

  onSlotDragOver(event: DragEvent, staff: ScheduleStaff, slot: Date): void {
    if (!this.draggedAppointment) {
      return;
    }

    event.preventDefault();

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }

    this.dragOverStaffId = staff.id;

    this.dragOverSlotKey = this.getSlotKey(slot);
  }

  onSlotDragLeave(staff: ScheduleStaff, slot: Date): void {
    if (
      this.dragOverStaffId === staff.id &&
      this.dragOverSlotKey === this.getSlotKey(slot)
    ) {
      this.dragOverStaffId = null;

      this.dragOverSlotKey = null;
    }
  }

  async onAppointmentDrop(
    event: DragEvent,
    staff: ScheduleStaff,
    slot: Date,
  ): Promise<void> {
    event.preventDefault();

    const appointment = this.draggedAppointment;

    this.dragOverStaffId = null;

    this.dragOverSlotKey = null;

    if (!appointment || this.isMovingAppointment) {
      return;
    }

    const oldStart = dayjs(appointment.start_time);

    const durationMinutes = Math.max(
      this.slotMinutes,

      dayjs(appointment.end_time).diff(oldStart, "minute") ||
        Number((appointment as any).total_duration_minutes) ||
        this.slotMinutes,
    );

    const newStart = dayjs(slot);

    const newEnd = newStart.add(durationMinutes, "minute");

    const currentStaffMatches = this.appointmentBelongsToStaff(
      appointment,
      staff,
    );

    if (currentStaffMatches && oldStart.isSame(newStart, "minute")) {
      this.onAppointmentDragEnd();

      return;
    }

    const confirmation = await Swal.fire({
      title: "Move appointment?",

      html: `
          <div style="text-align:left">
            <p>
              <strong>
                ${this.escapeHtml(
                  (appointment as any)?.customer?.full_name || "Appointment",
                )}
              </strong>
            </p>

            <p>
              ${this.escapeHtml(this.getCompactServiceNames(appointment))}
            </p>

            <p>
              <strong>New staff:</strong>
              ${this.escapeHtml(staff.name)}
            </p>

            <p>
              <strong>New time:</strong>
              ${newStart.format("ddd, MMM D")}
              at
              ${newStart.format("h:mm A")}
            </p>
          </div>
        `,

      icon: "question",

      showCancelButton: true,

      confirmButtonText: "Move appointment",

      cancelButtonText: "Cancel",
    });

    if (!confirmation.isConfirmed) {
      this.onAppointmentDragEnd();

      return;
    }

    this.persistAppointmentMove(
      appointment,
      staff,
      newStart.toDate(),
      newEnd.toDate(),
    );
  }

  /* =========================================================
     Persist drag
     ========================================================= */

  private persistAppointmentMove(
    appointment: Appointment,
    staff: ScheduleStaff,
    newStart: Date,
    newEnd: Date,
  ): void {
    const appointmentId = (appointment as any).appointment_id;

    this.isMovingAppointment = true;

    this.ApiService.request(
      API_ENDPOINTS.owner.appointments.detail(appointmentId),
      "PATCH",
      {
        staff_id: staff.id,

        start_time: newStart.toISOString(),
      },
    ).subscribe({
      next: (response: any) => {
        const index = this.appointmentsData.findIndex(
          (item: any) => item.appointment_id === appointmentId,
        );

        const updated = {
          ...(index >= 0 ? this.appointmentsData[index] : appointment),

          ...(response || {}),

          start_time: response?.start_time || newStart.toISOString(),

          end_time: response?.end_time || newEnd.toISOString(),

          staff: response?.staff || {
            ...(appointment as any).staff,

            user_id: staff.id,

            id: staff.id,

            full_name: staff.name,

            email: staff.email,

            job_title: staff.jobTitle,
          },
        } as Appointment;

        if (index >= 0) {
          this.appointmentsData[index] = updated;
        }

        if (
          this.selectedAppointment &&
          (this.selectedAppointment as any).appointment_id === appointmentId
        ) {
          this.selectedAppointment = {
            ...updated,
          };
        }

        this.applyFilters();

        this.isMovingAppointment = false;

        this.onAppointmentDragEnd();

        Swal.fire({
          title: "Appointment moved",

          text: `${staff.name} • ${dayjs(newStart).format("MMM D, h:mm A")}`,

          icon: "success",

          timer: 2600,

          showConfirmButton: false,

          toast: true,

          position: "top-end",
        });
      },

      error: (error) => {
        console.error("Error moving appointment:", error);

        this.isMovingAppointment = false;

        this.onAppointmentDragEnd();

        Swal.fire({
          title: "Unable to move appointment",

          text: this.extractApiError(
            error,
            "The selected employee or time is not available.",
          ),

          icon: "error",
        });

        /*
         * Restore authoritative
         * server state.
         */
        this.loadAppointments();

        this.loadScheduleStaff();
      },
    });
  }

  /* =========================================================
     API errors
     ========================================================= */

  private extractApiError(error: any, fallback: string): string {
    const body = error?.error;

    if (!body) {
      return fallback;
    }

    if (typeof body === "string") {
      return body;
    }

    if (typeof body?.detail === "string") {
      return body.detail;
    }

    const values = Object.values(body);

    for (const value of values) {
      if (typeof value === "string") {
        return value;
      }

      if (Array.isArray(value) && value.length) {
        return String(value[0]);
      }
    }

    return fallback;
  }

  private escapeHtml(value: string): string {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  /* =========================================================
     Existing formatting helpers
     ========================================================= */

  formatDate(dateString: string): string {
    return dayjs(dateString).format("MMM D, YYYY");
  }

  formatTime(dateString: string): string {
    return dayjs(dateString).format("h:mm A");
  }

  getInitials(name: string): string {
    if (!name) {
      return "NA";
    }

    const parts = name.trim().split(" ");

    if (parts.length === 1) {
      return parts[0][0].toUpperCase();
    }

    return parts[0][0].toUpperCase() + parts[parts.length - 1][0].toUpperCase();
  }

  getStatusClass(status: string): string {
    switch (this.normalizeAppointmentStatus(status)) {
      case "confirmed":
        return "status-booked";

      case "pending":
        return "status-pending";

      case "cancelled":
      case "no_show":
        return "status-cancelled";

      case "completed":
        return "status-complete";

      default:
        return "status-default";
    }
  }

  formatStatusLabel(status: string | null | undefined): string {
    const normalized = (status || "").trim().toLowerCase();

    switch (normalized) {
      case "pending":
        return "Pending";

      case "confirmed":
      case "booked":
        return "Confirmed";

      case "cancelled":
        return "Cancelled";

      case "completed":
      case "complete":
        return "Completed";

      case "no_show":
        return "No Show";

      case "paid":
        return "Paid";

      case "failed":
        return "Failed";

      case "refunded":
        return "Refunded";

      default:
        return normalized
          ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
          : "N/A";
    }
  }

  formatPaymentMethod(method: string | null | undefined): string {
    switch ((method || "").trim().toLowerCase()) {
      case "card":
        return "Card";

      case "cash":
        return "Cash";

      case "wallet":
        return "Wallet";

      case "bank":
        return "Bank Transfer";

      default:
        return "N/A";
    }
  }

  /* =========================================================
     Appointment display helpers
     ========================================================= */

  getServiceNames(appointment: any): string {
    if (!appointment.items || appointment.items.length === 0) {
      return "No services";
    }

    const names = appointment.items
      .map((item: any) => item.service?.name)
      .filter(Boolean)
      .join(", ");

    return `${appointment.items.length} service(s): ${names}`;
  }

  getServiceDetails(item: any): {
    name: string;
    duration_minutes: string;
    price: string;
  } {
    return {
      name: item?.service?.name || "N/A",

      duration_minutes: item?.service?.duration_minutes
        ? `${item.service.duration_minutes} min`
        : "N/A",

      price: item?.service?.price ? `$${item.service.price}` : "$0.00",
    };
  }

  getSalonId(appointment: any): string {
    return String(
      appointment?.salon?.salon_id ??
        appointment?.salon?.id ??
        appointment?.salon_id ??
        "",
    );
  }

  getSalonName(appointment: any): string {
    return (
      appointment?.salon?.name || appointment?.salon_name || "Unknown Salon"
    );
  }

  /* =========================================================
     Add appointment
     ========================================================= */

  openAddAppointmentModal(): void {
    const dialogRef = this.dialog.open(AppointmentsAddComponent, {
      panelClass: DialogPositionClass.right,
    });

    dialogRef.componentInstance.appointmentCreated.subscribe(() => {
      this.loadAppointments();

      if (this.calendarView === "day") {
        this.loadScheduleStaff();
      }
    });
  }

  /* =========================================================
     Details
     ========================================================= */

  openAppointmentModal(appointment: Appointment): void {
    this.selectedAppointment = {
      ...appointment,
    };

    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;

    this.selectedAppointment = null;

    this.isUpdatingStatus = false;
  }

  /* =========================================================
     Status update
     ========================================================= */

  updateAppointmentStatus(newStatus: string): void {
    if (!this.selectedAppointment) {
      return;
    }

    const normalizedStatus = (newStatus || "").trim().toLowerCase();

    const allowedStatuses = [
      "pending",
      "confirmed",
      "cancelled",
      "completed",
      "no_show",
    ];

    if (!allowedStatuses.includes(normalizedStatus)) {
      return;
    }

    this.isUpdatingStatus = true;

    const appointmentId = (this.selectedAppointment as any).appointment_id;

    this.ApiService.request(
      API_ENDPOINTS.owner.appointments.updateStatus(appointmentId),
      "PATCH",
      {
        status: normalizedStatus,
      },
    ).subscribe({
      next: (response: any) => {
        if (this.selectedAppointment) {
          const index = this.appointmentsData.findIndex(
            (app: any) =>
              app.appointment_id ===
              (this.selectedAppointment as any).appointment_id,
          );

          const updatedAppointment = {
            ...this.selectedAppointment,

            ...(response || {}),

            status: response?.status || normalizedStatus,

            updated_at: response?.updated_at || new Date().toISOString(),
          } as Appointment;

          if (index !== -1) {
            this.appointmentsData[index] = {
              ...this.appointmentsData[index],

              ...updatedAppointment,
            };
          }

          this.selectedAppointment = updatedAppointment;

          this.applyFilters();
        }

        Swal.fire({
          title: "Status Updated!",

          text:
            normalizedStatus === "completed"
              ? "Appointment completed and payment marked as paid by card."
              : "Appointment status updated successfully",

          icon: "success",

          timer: 5000,

          showConfirmButton: false,

          toast: true,

          position: "top-end",
        });

        this.isUpdatingStatus = false;
      },

      error: (error) => {
        console.error("Error updating appointment status:", error);

        this.isUpdatingStatus = false;

        Swal.fire({
          title: "Update Failed",

          text: this.extractApiError(
            error,
            "Failed to update appointment status. Please try again.",
          ),

          icon: "error",
        });
      },
    });
  }

  getAvailableActions(status: string): string[] {
    const normalizedStatus = this.normalizeAppointmentStatus(status);

    return ["pending", "confirmed", "cancelled", "completed", "no_show"].filter(
      (candidate) => candidate !== normalizedStatus,
    );
  }

  private normalizeAppointmentStatus(status: string): string {
    const normalized = (status || "").trim().toLowerCase();

    if (normalized === "booked") {
      return "confirmed";
    }

    if (normalized === "complete") {
      return "completed";
    }

    return normalized;
  }

  canUpdateStatus(status: string): boolean {
    return this.getAvailableActions(status).length > 0;
  }

  getActionButtonClass(action: string): string {
    switch ((action || "").toLowerCase()) {
      case "confirmed":
        return "action-confirm";

      case "cancelled":
      case "no_show":
        return "action-cancel";

      case "completed":
        return "action-complete";

      default:
        return "action-default";
    }
  }

  formatActionLabel(action: string): string {
    return this.formatStatusLabel(action);
  }

  getTotalPrice(appointment: Appointment): string {
    return (appointment as any).total_price || "0.00";
  }

  getTotalDuration(appointment: Appointment): string {
    const minutes = (appointment as any).total_duration_minutes || 0;

    const hours = Math.floor(minutes / 60);

    const remainingMinutes = minutes % 60;

    if (hours > 0) {
      return `${hours}h ${remainingMinutes}m`;
    }

    return `${remainingMinutes}m`;
  }

  /* =========================================================
     TrackBy
     ========================================================= */

  trackStaff(_index: number, staff: ScheduleStaff): string {
    return `${staff.salonId}:${staff.id}`;
  }

  trackSlot(_index: number, slot: Date): number {
    return slot.getTime();
  }

  trackAppointment(_index: number, appointment: any): string {
    return String(appointment?.appointment_id);
  }

  trackCalendarDate(_index: number, date: Date): number {
    return date.getTime();
  }
}
